﻿import React, {useEffect, useState} from 'react';
import '../styles/SuccessNotifier.css';
import axios from "axios";
import {useNavigate} from "react-router-dom";
import {CustomButton} from "../../Shared/components/CustomButton.jsx";
import { isAbortError } from '../isAbortError.js';
import { useAbortController } from '../../Shared/hooks/useAbortController.js';
import { validateReturnTo } from '../../Shared/utils/authReturnTo.js';

export default function SuccessNotifier({
    isOpened,
    setIsOpened,
    email,
    type,
    resetHandler,
    returnTo,
}) {

    const [title, setTitle] = useState();
    const [isResending, setIsResending] = useState(false);
    const [resendFeedback, setResendFeedback] = useState(null);
    const navigate = useNavigate();
    const { nextSignal } = useAbortController();

    useEffect(() => {
        if (!isOpened) {
            setResendFeedback(null);
            setIsResending(false);
        }
    }, [isOpened]);

    useEffect(() => {
        setResendFeedback(null);
    }, [type, email]);

    useEffect(() => {
        switch (type) {
            case 'signup':
                setTitle("Check your email");
                break;
            case 'forgot-password':
            case 'login-resend':
                setTitle("Reset link has been sent");
                break;
            case 'recover-password':
                setTitle("Your password has been updated")
        }
    }, [type])

    function generateText() {
        switch (type) {
            case 'signup':
                return (<>
                    <p>We’ve sent a confirmation link to your email address <span>{email}</span>.</p>
                    <p>Please open the email and click the link to verify your account.</p>
                </>);
            case 'forgot-password':
            case 'login-resend':
                return (<>
                    <p>We’ve sent a password reset link to your email </p>
                    <p><span>{email}</span>.</p>
                    <p>Please check your inbox (and spam folder just in case).</p>
                </>);
            case 'recover-password':
                return "You can now sign in to your account with your new password."
        }
    }

    function generateOptions() {
        switch (type) {
            case 'signup':
            case 'forgot-password':
                return (<>
                    <p onClick={isResending ? undefined : resentEmail}>
                        Didn’t receive the email? <span>{isResending ? 'Sending…' : 'Resend'}</span>
                    </p>
                    <p onClick={() => setIsOpened(false)}>Entered the wrong email? <span>Change email</span></p>
                </>)
            case 'login-resend':
                return (<>
                    <p onClick={isResending ? undefined : resentEmail}>
                        Didn’t receive the email? <span>{isResending ? 'Sending…' : 'Resend'}</span>
                    </p>
                </>)
            case 'recover-password':
                return (<div className="go-to-sign-in-button">
                    <CustomButton text="Go to Sign in" onClick={() => navigate('/login')}/>
                </div>)
        }
    }

    function backgroundClick(event) {
        if (event.target === event.currentTarget) {
            setIsOpened(false)
        }
    }

    function getResendSuccessMessage() {
        if (type === 'forgot-password') {
            return 'We’ve sent the reset link again. Please check your inbox.';
        }

        return 'We’ve sent the confirmation email again. Please check your inbox.';
    }

    async function resentEmail() {
        if (isResending) {
            return;
        }

        if (resetHandler) {
            resetHandler();
            return;
        }

        if ((type === 'forgot-password' || type === 'login-resend' || type === 'signup') && email) {
            const payload = { email };
            const validatedReturnUrl = validateReturnTo(returnTo);
            if (validatedReturnUrl) {
                payload.returnUrl = validatedReturnUrl;
            }

            setIsResending(true);
            setResendFeedback(null);

            try {
                await axios.post(`${import.meta.env.VITE_BASE_URL}/account/resend`, payload, {
                    signal: nextSignal('resend'),
                });
                setResendFeedback({
                    type: 'success',
                    text: getResendSuccessMessage(),
                });
            } catch (error) {
                if (!isAbortError(error)) {
                    console.error('Resend failed:', error);
                    setResendFeedback({
                        type: 'error',
                        text: 'We couldn’t resend the email. Please try again in a moment.',
                    });
                }
            } finally {
                setIsResending(false);
            }
        }
    }

    function CheckIcon(props) {
        return (
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
                <rect x="1" y="1" width="30" height="30" rx="15" stroke="#518681" strokeWidth="2"/>
                <path d="M8 16L13.3333 21L24 11" stroke="#518681" strokeWidth="2" strokeLinecap="round"/>
            </svg>
        );
    }

    return (
        isOpened && (
            <div className="modal wrapper" onClick={backgroundClick}>
                <div className="content">
                    <div className="modal__header">
                        <div className="left-part">
                            <CheckIcon className="payment-icon"/>
                            <h1 className="payment-title">{title}</h1>
                        </div>
                        <svg
                            className="payment-modal__close"
                            width="30"
                            height="30"
                            viewBox="0 0 30 30"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            onClick={() => setIsOpened(false)}
                        >
                            <path d="M6 6L22.9706 22.9706" stroke="#032320" strokeWidth="2" strokeLinecap="round"/>
                            <path d="M6 23L22.9706 6.02944" stroke="#032320" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                    </div>
                    <div className="divider"></div>
                    <div className="main-text">
                        {generateText()}
                    </div>
                    {resendFeedback && (
                        <div className={`success-notifier__feedback success-notifier__feedback--${resendFeedback.type}`}>
                            {resendFeedback.text}
                        </div>
                    )}
                    <div className="options">
                        {generateOptions()}
                    </div>
                </div>
            </div>
        )
    );
}